import { Injectable } from '@angular/core';
import { PaginatedState, PaginatedStateModel } from '@shared/ui';

export interface WorkspaceMemberData {
  id: string;
  username: string;
  avatarUrl: string | undefined;
  emailVerified: boolean;
  membershipId: string | undefined;
}

export interface MembershipData {
  workspaceId: string;
  membershipId: string | undefined;
}

export interface WorkspaceMembersStateModel extends PaginatedStateModel<WorkspaceMemberData> {
  workspaceId: string | undefined;
  memory: Record<string, string>;
  memberOperationError: string | undefined;
}

export const NO_MEMBERSHIP = 'NO_MEMBERSHIP';

const PAGE_SIZE = 20;

@Injectable()
export class WorkspaceMembersState extends PaginatedState<WorkspaceMemberData, WorkspaceMembersStateModel> {
  constructor() {
    super(
      { initialPageSize: PAGE_SIZE },
      {
        workspaceId: undefined,
        memory: {},
        memberOperationError: undefined,
      }
    );
  }

  // ============================================================================
  // Custom Selectors
  // ============================================================================

  public readonly workspaceId$ = this.select((state) => state.workspaceId);
  public readonly memory$ = this.select((state) => state.memory);
  public readonly memberOperationError$ = this.select((state) => state.memberOperationError);

  // Legacy aliases (delegate to PaginatedState)
  public readonly workspaceMembers$ = this.items$;
  public readonly membersLoading$ = this.loading$;
  public readonly hasMoreMembers$ = this.hasMoreItems$;
  public readonly membersSearchTerm$ = this.searchTerm$;
  public readonly membersPreviousTokens$ = this.previousTokens$;
  public readonly membersNextToken$ = this.nextToken$;
  public readonly membersTotalCount$ = this.totalCount$;
  public readonly membersPageSize$ = this.pageSize$;
  public readonly membersCounting$ = this.counting$;

  // ============================================================================
  // Custom Updaters
  // ============================================================================

  public readonly setWorkspaceId = this.updater(
    (state, workspaceId: string) =>
      ({
        ...state,
        workspaceId,
        items: this.itemsAdapter.removeAll(state.items),
        hasMoreItems: true,
        previousPageTokens: [],
        nextToken: undefined,
        loading: true,
      } as WorkspaceMembersStateModel)
  );

  public readonly extendMemory = this.updater((state, membershipIds: Record<string, string>) => ({
    ...state,
    memory: {
      ...state.memory,
      ...membershipIds,
    },
  }));

  public readonly updateUserInMemory = this.updater(
    (state, { userId, membershipId }: { userId: string; membershipId: string | undefined }) => {
      if (membershipId === undefined) {
        const newMemory = { ...state.memory };
        delete newMemory[userId];
        return { ...state, memory: newMemory };
      }
      return {
        ...state,
        memory: {
          ...state.memory,
          [userId]: membershipId,
        },
      };
    }
  );

  public readonly markMembershipPending = this.updater((state, userId: string) => {
    const newMemory = { ...state.memory };
    delete newMemory[userId];
    return {
      ...state,
      memory: newMemory,
      items: this.itemsAdapter.updateOne({ id: userId, changes: { membershipId: undefined } }, state.items),
    };
  });

  public readonly setMemberOperationError = this.updater((state, memberOperationError: string | undefined) => ({
    ...state,
    memberOperationError,
  }));
}
